import {useCallback, useEffect, useRef, type KeyboardEvent, type ReactNode} from 'react';
import {cx} from './cx';

/**
 * The confirm bottom sheet.
 *
 * Placing an order goes through this and never straight from the button — the
 * design package is explicit about it, and the sheet's weight comes from being
 * the **only** one on the surface. Do not add an "are you sure" to anything
 * else; a second one makes the first one furniture.
 *
 * # What the design drew, and what had to be added
 *
 * Drawn: Sticky Rice ground (not white — the sheet is the same ground raised,
 * not a card), top corners rounded, a title, a body line, a solid red confirm
 * and an ink-outline cancel. No drag handle, no ✕, no animation. All kept.
 *
 * Added, because a modal that traps a guest is worse than no modal:
 *
 *   * `role="dialog"` + `aria-modal` + a labelled title.
 *   * Focus moves into the sheet on open and **returns to the element that
 *     opened it** on close. A guest whose focus is left on a hidden page cannot
 *     find their way back with a keyboard or a switch.
 *   * Escape cancels.
 *   * Focus is trapped in a Tab cycle while it is open.
 *
 * # Tapping the scrim does not dismiss, on purpose
 *
 * This sheet's whole job is to make sending an order a deliberate act. A scrim
 * tap is the most common accidental gesture on a phone held one-handed, and the
 * two outcomes are not symmetric: dismissing by accident costs a tap, sending by
 * accident costs a wrong order in a kitchen. The design's scrim carries no
 * handler either. `ยกเลิก` is the way out and it is 48px.
 *
 * `Sheet` renders nothing when `open` is false — it is not hidden with CSS,
 * because a hidden dialog still holds focusable children.
 */
export function Sheet({
  open,
  title,
  titleId = 'pk-sheet-title',
  onCancel,
  children,
  className,
}: {
  open: boolean;
  /** Localised. Becomes the dialog's accessible name. */
  title: ReactNode;
  titleId?: string;
  /** Escape, and the cancel button the caller renders in `children`. */
  onCancel: () => void;
  /** The body copy and the actions. Put the confirm first, cancel second. */
  children: ReactNode;
  className?: string;
}) {
  const sheetRef = useRef<HTMLDivElement | null>(null);
  const returnTo = useRef<Element | null>(null);

  useEffect(() => {
    if (!open) return;
    returnTo.current = globalThis.document?.activeElement ?? null;
    // The sheet itself takes focus rather than the confirm button: landing on
    // "yes" means a stray Enter sends the order, which is the exact thing this
    // sheet exists to prevent.
    sheetRef.current?.focus();
    return () => {
      const previous = returnTo.current;
      if (previous instanceof HTMLElement) previous.focus();
    };
  }, [open]);

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onCancel();
        return;
      }
      if (event.key !== 'Tab') return;

      const root = sheetRef.current;
      if (!root) return;
      const focusable = root.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      const active = globalThis.document?.activeElement;

      if (event.shiftKey && (active === first || active === root)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [onCancel],
  );

  if (!open) return null;

  return (
    <div className="pk-scrim">
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        className={cx('pk-sheet', className)}
      >
        <h2 id={titleId} className="pk-title">
          {title}
        </h2>
        {children}
      </div>
    </div>
  );
}
